import React from "react";
import LegalLayout from "@/components/LegalLayout";

export default function Cookies() {
  return (
    <LegalLayout
      eyebrow="// LEGAL"
      title="Cookie Policy"
      lastUpdated="February 22, 2026"
    >
      <p>
        This Cookie Policy explains how <strong>BroadcastFlow AI</strong>
        (&ldquo;BroadcastFlow&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) uses
        cookies and similar tracking technologies on our website and inside
        the dashboard.
      </p>

      <h2>1. What Are Cookies?</h2>
      <p>
        Cookies are small text files placed on your device by websites you
        visit. They are widely used to make websites work, or work more
        efficiently, and to provide information to the site operator. Similar
        technologies include local storage, session storage, and pixel tags;
        for simplicity we refer to them all as &ldquo;cookies&rdquo; in this
        policy.
      </p>

      <h2>2. How We Use Cookies</h2>
      <p>
        BroadcastFlow uses only a small number of cookies, all of which fall
        into one of two categories: <strong>strictly necessary</strong> or
        <strong>{" "}preferences</strong>. We do <strong>not</strong> use
        third-party advertising cookies, cross-site tracking pixels, or
        marketing-attribution cookies.
      </p>

      <h3>2.1 Strictly necessary cookies</h3>
      <p>
        These cookies are required for the Service to function. They cannot be
        turned off in your browser without breaking core features such as
        sign-in, security, and persisting your draft work.
      </p>
      <ul>
        <li>
          <strong><code>session_token</code></strong> &mdash; keeps you signed
          in. Set on login; removed on sign out or expiry. HTTP-only, Secure,
          SameSite=Lax. Lifetime: 30 days.
        </li>
        <li>
          <strong><code>csrf_token</code></strong> &mdash; protects against
          cross-site request forgery on state-changing requests. Lifetime: per
          session.
        </li>
        <li>
          <strong><code>x-station-id</code></strong> (local storage) &mdash;
          remembers which station you last selected in the dashboard switcher.
        </li>
      </ul>

      <h3>2.2 Preference cookies</h3>
      <p>
        Small, first-party flags that remember your UI choices so the dashboard
        feels consistent between visits.
      </p>
      <ul>
        <li>
          <strong><code>theme</code></strong> &mdash; remembered if you toggle
          a theme variant.
        </li>
        <li>
          <strong><code>language</code></strong> &mdash; the locale you
          selected from the in-app Language Switcher.
        </li>
        <li>
          <strong><code>bfa_iframe_banner_dismissed_v1</code></strong>{" "}
          (session storage) &mdash; remembers that you dismissed the
          &ldquo;sandboxed preview&rdquo; banner this session.
        </li>
      </ul>

      <h3>2.3 Third-party cookies (only when you connect those services)</h3>
      <p>
        When you connect a third-party platform (e.g., Meta, X, YouTube,
        Stripe), the OAuth or checkout flow may set cookies for that provider
        in your browser. We do not control or read those cookies. They are
        governed by the privacy policy of the respective provider.
      </p>

      <h2>3. Analytics</h2>
      <p>
        We use lightweight, first-party usage telemetry (e.g., aggregate event
        counts, error logs) to operate, secure, and improve the Service. This
        data is collected with your account, not via a third-party analytics
        cookie, and is described in our{" "}
        <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>4. Your Choices</h2>
      <p>
        You can control or delete cookies through your browser settings &mdash;
        consult your browser&rsquo;s help pages for instructions. Blocking
        strictly-necessary cookies will prevent you from signing in or using
        most of the Service.
      </p>
      <p>
        If we ever introduce optional analytics or advertising cookies in the
        future, we will update this policy and provide a consent banner before
        setting them.
      </p>

      <h2>5. Do-Not-Track</h2>
      <p>
        Because we do not use cross-site tracking or third-party advertising
        cookies, the Service does not respond differently to browser
        &ldquo;Do-Not-Track&rdquo; signals.
      </p>

      <h2>6. Changes to This Policy</h2>
      <p>
        We may update this Cookie Policy from time to time. The &ldquo;Last
        updated&rdquo; date above will always reflect the most recent revision.
      </p>

      <h2>7. Contact</h2>
      <p>
        Questions about cookies on BroadcastFlow AI?
        Write to{" "}<a href="mailto:privacy@broadcastflow.ai">privacy@broadcastflow.ai</a>.
      </p>

      <p className="text-xs text-ink-muted mt-12 pt-6 border-t border-line">
        This Cookie Policy is provided as a starting point and has not been
        reviewed by legal counsel. Cookie / e-Privacy rules vary by country;
        consult a qualified lawyer (and, if you serve EU users, an e-Privacy /
        GDPR specialist) before relying on it in production.
      </p>
    </LegalLayout>
  );
}
